// Ocean Insight Pro Mobile - Weather Redux Slice

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WeatherForecast, WeatherAlert, GeoCoordinate } from '../types';
import { WeatherApiService } from '../services/weatherApiService';

// Define the weather state interface
interface WeatherState {
  current: WeatherForecast | null;
  forecast: WeatherForecast[];
  hourlyForecast: WeatherForecast[];
  alerts: WeatherAlert[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

// Initial state
const initialState: WeatherState = {
  current: null,
  forecast: [],
  hourlyForecast: [],
  alerts: [],
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunks for API calls
export const fetchCurrentWeather = createAsyncThunk(
  'weather/fetchCurrent',
  async (location: GeoCoordinate) => {
    const current = await WeatherApiService.getCurrentWeather(location);
    return current;
  }
);

export const fetchWeatherForecast = createAsyncThunk(
  'weather/fetchForecast',
  async (location: GeoCoordinate) => {
    const forecast = await WeatherApiService.getWeatherForecast(location, 16);
    return forecast;
  }
);

export const fetchHourlyWeatherForecast = createAsyncThunk(
  'weather/fetchHourlyForecast',
  async (location: GeoCoordinate) => {
    return WeatherApiService.getHourlyWeatherForecast(location, 16);
  }
);

export const fetchWeatherAlerts = createAsyncThunk(
  'weather/fetchAlerts',
  async (location: GeoCoordinate) => {
    const alerts = await WeatherApiService.getWeatherAlerts(location);
    return alerts;
  }
);

// Weather slice
const weatherSlice = createSlice({
  name: 'weather',
  initialState,
  reducers: {
    clearWeatherData: (state) => {
      state.current = null;
      state.forecast = [];
      state.hourlyForecast = [];
      state.alerts = [];
      state.error = null;
    },
    setWeatherError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch current weather
    builder
      .addCase(fetchCurrentWeather.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentWeather.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchCurrentWeather.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch current weather';
      });

    // Fetch weather forecast
    builder
      .addCase(fetchWeatherForecast.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeatherForecast.fulfilled, (state, action) => {
        state.loading = false;
        state.forecast = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchWeatherForecast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch weather forecast';
      });

    // Fetch weather alerts
    builder
      .addCase(fetchWeatherAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeatherAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchWeatherAlerts.rejected, (state, _action) => {
        state.loading = false;
        // Don't set error for weather alerts failures - they're handled gracefully
        // Weather alerts are optional and only work in US territories
        // state.error = action.error.message || 'Failed to fetch weather alerts';
        state.alerts = []; // Just clear alerts on error
      });

    builder
      .addCase(fetchHourlyWeatherForecast.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHourlyWeatherForecast.fulfilled, (state, action) => {
        state.loading = false;
        state.hourlyForecast = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchHourlyWeatherForecast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch hourly weather forecast';
      });
  },
});

// Export actions
export const { clearWeatherData, setWeatherError } = weatherSlice.actions;

// Export reducer
export default weatherSlice.reducer;
